import { useCallback, useMemo, useState, type ReactNode } from 'react';
import {
  Button,
  Dialog,
  DialogBody,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Spinner,
} from '@monksflow/monks-ui';
import { AlertCircleIcon } from 'lucide-react';
import { EmptyState } from '@/shared/ui';
import { FormSidePanel } from '@/shared/ui/layout';
import {
  DEFAULT_PROJECT_ROLE_NAME,
  MemberListItem,
  type ProjectMember,
  type RoleAssignment,
  type RoleSelectorOption,
  type WorkspaceUser,
} from '@/entities/project-member';
import type { MembershipSnapshot } from '../../lib/approval-step-shortfalls';
import { AddMembersSection } from '../AddMembersSection';
import { useTranslation } from 'react-i18next';

/**
 * Optional per-member role controls: platform roles on Projects, access levels
 * on Trackers. Someone staged in the modal starts on the default role, else the
 * first one listed.
 */
interface RolesConfig {
  enabled: boolean;
  canEdit: boolean;
  availableRoles: RoleSelectorOption[];
  assignments: Array<Pick<RoleAssignment, 'identityId' | 'roleId'>>;
  isLoading: boolean;
  /** Accessible name for a member's picker; defaults to "Role: …". */
  selectorLabel?: (memberName: string, roleName: string) => string;
}

export interface ManageMembersModalProps {
  open: boolean;
  onClose: () => void;
  /** Dialog heading (e.g. "Project members", "Share tracker"). */
  title: string;
  /**
   * Where the shell opens. `panel` is the redesign's right-hand sheet; the
   * caller decides, because Projects and Trackers both use this shell and only
   * one of them is being redesigned.
   */
  surface?: 'dialog' | 'panel';
  /** Owner (creator) — always shown, non-removable. Absent when nobody owns the resource. */
  ownerId?: string;
  /** Server-truth current members. */
  members: ProjectMember[];
  /** Workspace users for the add picker. */
  workspaceUsers: WorkspaceUser[];
  isLoadingUsers: boolean;
  isLoadingMembers?: boolean;
  searchInput: string;
  onSearchChange: (value: string) => void;
  /** Commit the final member-id set + optional role assignments. */
  onSave: (memberIds: string[], roles?: Array<{ userId: string; roleId: string }>) => void;
  isSaving: boolean;
  saveLabel?: string;
  savingLabel?: string;
  /**
   * Whether the caller may change the member set. False renders the list
   * read-only: no add picker, no remove control, no save. Defaults to true so
   * callers that gate the whole surface upstream stay as they are.
   */
  canManage?: boolean;
  /** Optional note above the people list (e.g. tracker access explanation). */
  hint?: ReactNode;
  /** Heading for the members list. */
  peopleHeading?: string;
  roles?: RolesConfig;
  /**
   * The current members could not be loaded. Shows this in place of the list,
   * with Save held off: a member set staged against a list that never arrived
   * would remove everyone the host failed to read.
   */
  loadError?: { title: string; description: string; retryLabel: string; onRetry: () => void };
  /** Shown above the actions while changes are staged, given the membership they would leave. */
  renderChangeNotice?: (staged: MembershipSnapshot) => ReactNode;
}

/**
 * Presentational membership-management modal shared by every "who has
 * access" surface (project members, tracker sharing, …). It owns the
 * in-memory staging (add / remove) and calls `onSave(memberIds)`; the host
 * injects the data source (members + workspace users) and the mutation.
 * Keeping the shell here means visual/UX changes propagate to every
 * consumer at once.
 */
export function ManageMembersModal(props: ManageMembersModalProps) {
  const { t } = useTranslation('projects');

  const {
    open,
    onClose,
    title,
    surface = 'dialog',
    ownerId,
    members,
    workspaceUsers,
    isLoadingUsers,
    isLoadingMembers,
    searchInput,
    onSearchChange,
    onSave,
    isSaving,
    canManage = true,
    saveLabel = t('manageProjectMembers.manageMembersModal.save'),
    savingLabel = t('manageProjectMembers.manageMembersModal.saving'),
    hint,
    peopleHeading = t('manageProjectMembers.manageMembersModal.peopleWithAccess'),
    roles,
    loadError,
  } = props;

  // Staged edits — reset every time the host remounts the modal on open.
  const [removedIds, setRemovedIds] = useState<string[]>([]);
  const [addedUsers, setAddedUsers] = useState<WorkspaceUser[]>([]);
  const [roleMap, setRoleMap] = useState<Record<string, string>>({});

  const defaultRoleId = useMemo(() => {
    // Someone staged with a role the caller cannot assign would sink the whole save.
    const available = (roles?.availableRoles ?? []).filter((r) => r.assignable !== false);
    return (
      available.find((r) => r.roleName === DEFAULT_PROJECT_ROLE_NAME)?.id ??
      available.at(0)?.id ??
      ''
    );
  }, [roles?.availableRoles]);

  const stagedIds = useMemo(() => new Set(addedUsers.map((user) => user.id)), [addedUsers]);

  // The platform lets a caller revoke a role only when they could grant it, so removing a member
  // who holds one the caller cannot assign, or moving them off it, sinks the whole save.
  const holdersOfClosedRoles = useMemo(() => {
    const closedRoleIds = new Set(
      (roles?.availableRoles ?? []).filter((r) => r.assignable === false).map((r) => r.id),
    );
    return new Set(
      (roles?.assignments ?? [])
        .filter((a) => closedRoleIds.has(a.roleId))
        .map((a) => a.identityId),
    );
  }, [roles?.availableRoles, roles?.assignments]);

  const assignedRoleIds = useMemo(
    () => new Map((roles?.assignments ?? []).map((a) => [a.identityId, a.roleId])),
    [roles?.assignments],
  );

  const getEffectiveRoleId = useCallback(
    (memberId: string): string | undefined => {
      if (roleMap[memberId]) return roleMap[memberId];
      const assignedRoleId = assignedRoleIds.get(memberId);
      if (assignedRoleId) return assignedRoleId;
      // Naming the default for an existing member would claim a role they do not
      // hold; someone staged here takes it because they are about to be granted one.
      return stagedIds.has(memberId) ? defaultRoleId : undefined;
    },
    [roleMap, assignedRoleIds, defaultRoleId, stagedIds],
  );

  /**
   * The roles a member would hold once saved. A picked role replaces the one they hold, but is
   * added beside several, since nothing here says which of them it was meant to replace; the
   * server applies the same rule.
   */
  const rolesAfterSave = (memberId: string): ReadonlySet<string> => {
    const held = new Set(
      (roles?.assignments ?? []).filter((a) => a.identityId === memberId).map((a) => a.roleId),
    );
    const pickedRoleId = roleMap[memberId];
    if (pickedRoleId) {
      return held.size > 1 ? new Set([...held, pickedRoleId]) : new Set([pickedRoleId]);
    }
    if (held.size > 0) return held;
    // Someone staged here holds nothing yet and starts on the default role.
    const startingRoleId = getEffectiveRoleId(memberId);
    return startingRoleId ? new Set([startingRoleId]) : held;
  };

  const displayedMembers = useMemo<ProjectMember[]>(() => {
    const kept = members.filter((member) => !removedIds.includes(member.id));
    const staged: ProjectMember[] = addedUsers.map((user) => ({
      id: user.id,
      name: user.name,
      email: user.email ?? '',
      avatarUrl: user.avatarUrl ?? null,
      permissions: [],
      createdAt: '',
      updatedAt: '',
    }));
    return [...kept, ...staged].toSorted((a, b) => {
      if (a.id === ownerId) return -1;
      if (b.id === ownerId) return 1;
      return a.name.localeCompare(b.name);
    });
  }, [members, removedIds, addedUsers, ownerId]);

  const workingMemberIds = displayedMembers.map((member) => member.id);

  const hasRoleChanges = useMemo(() => {
    return Object.entries(roleMap).some(
      ([userId, roleId]) => assignedRoleIds.get(userId) !== roleId,
    );
  }, [roleMap, assignedRoleIds]);

  const rolesReady = !roles?.enabled || !roles.isLoading;
  const hasChanges = removedIds.length > 0 || addedUsers.length > 0 || hasRoleChanges;

  const handleAdd = (users: WorkspaceUser[]) => {
    const reAddedIds = users.map((user) => user.id);
    setRemovedIds((prev) => prev.filter((id) => !reAddedIds.includes(id)));
    setAddedUsers((prev) => {
      const known = new Set([...members.map((m) => m.id), ...prev.map((u) => u.id)]);
      const fresh = users.filter((user) => !known.has(user.id));
      return [...prev, ...fresh];
    });
    onSearchChange('');
  };

  const handleRemove = (memberId: string) => {
    setRoleMap((prev) =>
      Object.fromEntries(Object.entries(prev).filter(([id]) => id !== memberId)),
    );
    if (addedUsers.some((user) => user.id === memberId)) {
      setAddedUsers((prev) => prev.filter((user) => user.id !== memberId));
      return;
    }
    setRemovedIds((prev) => (prev.includes(memberId) ? prev : [...prev, memberId]));
  };

  const handleSave = () => {
    if (!canManage) return;
    const rolePayload = roles?.enabled
      ? displayedMembers
          .filter((m) => m.id !== ownerId)
          .flatMap((m) => {
            const roleId = getEffectiveRoleId(m.id);
            return roleId ? [{ userId: m.id, roleId }] : [];
          })
      : undefined;
    onSave(workingMemberIds, rolePayload);
  };

  // `onClose` alone — the host unmounts on its own timer. See `useModalState`.
  const closeIfIdle = () => {
    if (isSaving) return;
    onClose();
  };

  const isLoading = isLoadingMembers || !rolesReady;

  const body = loadError ? (
    <EmptyState
      size="compact"
      icon={<AlertCircleIcon />}
      title={loadError.title}
      description={loadError.description}
    >
      <Button variant="outline" onClick={loadError.onRetry}>
        {loadError.retryLabel}
      </Button>
    </EmptyState>
  ) : (
    <>
      {isLoading ? (
        <div className="flex justify-center py-10">
          <Spinner />
        </div>
      ) : (
        <div className="flex flex-col gap-8">
          {canManage && (
            <AddMembersSection
              workspaceUsers={workspaceUsers}
              existingMemberIds={workingMemberIds}
              onAdd={handleAdd}
              isAdding={isSaving}
              searchInput={searchInput}
              onSearchChange={onSearchChange}
              isLoadingUsers={isLoadingUsers}
            />
          )}

          <div className="flex flex-col gap-4">
            {hint ? <div className="text-sm text-text-secondary">{hint}</div> : null}
            <h3 className="text-base font-medium text-text-primary">{peopleHeading}</h3>

            <div className="flex flex-col gap-3">
              {displayedMembers.map((member) => {
                const effectiveRoleId = getEffectiveRoleId(member.id);
                const effectiveRoleName = roles?.availableRoles.find(
                  (r) => r.id === effectiveRoleId,
                )?.roleName;

                return (
                  <MemberListItem
                    key={member.id}
                    member={member}
                    isOwner={member.id === ownerId}
                    onRemove={() => handleRemove(member.id)}
                    canRemove={canManage}
                    disabled={isSaving}
                    {...(canManage &&
                      holdersOfClosedRoles.has(member.id) && {
                        lockedReason: t('projectMember.roleSelector.includesPermissionsYouLack'),
                      })}
                    {...(roles?.enabled && {
                      roleId: effectiveRoleId,
                      roleName: effectiveRoleName,
                      roles: roles.availableRoles,
                      roleSelectorLabel: roles.selectorLabel?.(
                        member.name,
                        effectiveRoleName ?? '',
                      ),
                      onRoleChange: (newRoleId: string) =>
                        setRoleMap((prev) => ({ ...prev, [member.id]: newRoleId })),
                      canEditRole: canManage && roles.canEdit,
                    })}
                  />
                );
              })}
            </div>
          </div>
        </div>
      )}
    </>
  );

  const changeNotice =
    canManage && hasChanges && props.renderChangeNotice
      ? props.renderChangeNotice({
          memberIds: new Set(workingMemberIds),
          holdsRole: (userId, roleId) => rolesAfterSave(userId).has(roleId),
        })
      : null;

  // Read-only leaves the surface's own dismiss as the only control: a footer
  // here would be a second button saying the same thing as the header's close.
  const buttons = canManage ? (
    <>
      <Button variant="outline" onClick={onClose} disabled={isSaving}>
        {t('manageProjectMembers.manageMembersModal.cancel')}
      </Button>
      <Button
        variant="default"
        onClick={handleSave}
        disabled={Boolean(loadError) || !hasChanges || !rolesReady || isSaving}
      >
        {isSaving ? savingLabel : saveLabel}
      </Button>
    </>
  ) : null;
  // `empty:hidden` drops the notice's slot, and its gap, when the notice renders nothing.
  const actions =
    buttons && changeNotice ? (
      <div className="flex w-full flex-col gap-sm">
        <div className="empty:hidden">{changeNotice}</div>
        <div className="flex justify-end gap-sm">{buttons}</div>
      </div>
    ) : (
      buttons
    );

  if (surface === 'panel') {
    return (
      <FormSidePanel
        open={open}
        onOpenChange={(next) => (!next ? closeIfIdle() : undefined)}
        title={title}
        footer={actions}
      >
        {body}
      </FormSidePanel>
    );
  }

  return (
    <Dialog open={open} onOpenChange={(next) => (!next ? closeIfIdle() : undefined)}>
      <DialogContent size="lg" aria-describedby={undefined}>
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
        </DialogHeader>

        <DialogBody>{body}</DialogBody>

        {actions ? <DialogFooter>{actions}</DialogFooter> : null}
      </DialogContent>
    </Dialog>
  );
}
